/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { DocumentsFormConfig } from "@/src/services/documentsFormService";
import Grid from "@mui/material/Grid";
import wizardStyle from "@/src/components/Form/wizardStyle";
import Typography from "@mui/material/Typography";
import MultipleFileUpload from "@/src/components/Form/Common/FileUpload/MultipleFileUpload";
import { filterMediaObjectsByFieldname } from "@/src/services/mediaObjects";
import { MissingDocumentCheckbox } from "./MissingDocumentCheckbox";
import FileUploadSection from "@/src/components/Form/Common/FileUploadSection/FileUploadSection";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import { MediaObject } from "@/src/types/Entities/MediaObject/MediaObject";
import React from "react";
import { useForm } from "@/src/provider/Form/FormProvider";
import { CorrespondenceHelp } from "./CorrespondenceHelp";

export type SectionElementsProps = {
  configs: DocumentsFormConfig[];
  product: AbstractCase;
  mediaObjectCollection: MediaObject[];
  onMissingDocumentsCheckboxChange: (documentType: string) => void;
  uploadIsBlocked: boolean;
  setUploadIsBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  refreshMediaObjectCollection: () => void;
  refreshCase: () => void;
} & Omit<ReturnType<typeof useMediaObjectCombinationRequest>, "isLoadingMediaObjectCombinationRequest">;

export const SectionElements = ({
  configs,
  product,
  updateAction,
  mediaObjectCombinationRequests,
  hasAtLeastOneFileOfType,
  mediaObjectCollection,
  onMissingDocumentsCheckboxChange,
  uploadIsBlocked,
  setUploadIsBlocked,
  refreshMediaObjectCollection,
  refreshCase,
}: SectionElementsProps) => {
  const { values, updateValue } = useForm();

  return (
    <>
      {configs.map((config) => {
        if (!config) {
          return null;
        }

        switch (config.type) {
          case "MultipleFileUpload": {
            const mediaObjectTypeName = config.mediaObjectTypeName || config.fieldname;

            return (
              <React.Fragment key={mediaObjectTypeName}>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  {config.headline && (
                    <Typography variant="h6" sx={(theme) => ({ color: theme.palette.text.primary, marginBottom: "1rem" })}>
                      {config.headline({ ...config, product })}
                    </Typography>
                  )}
                  {config.subHeadline && <Typography>{config.subHeadline({ ...config, product })}</Typography>}
                  {config.showCorrespondenceHelp && <CorrespondenceHelp />}
                  {!values?.missingDocuments?.includes(mediaObjectTypeName) && (
                    <MultipleFileUpload
                      {...config}
                      product={product}
                      description={config?.description({ ...config, product })}
                      fetchMediaObjects={refreshMediaObjectCollection}
                      mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, mediaObjectTypeName)}
                      disabled={uploadIsBlocked}
                    />
                  )}
                </Grid>
                {config.isRequired?.({ product }) && !filterMediaObjectsByFieldname(mediaObjectCollection, mediaObjectTypeName).length && (
                  <MissingDocumentCheckbox
                    product={product}
                    onChange={onMissingDocumentsCheckboxChange}
                    updateValues={updateValue}
                    documentType={mediaObjectTypeName}
                    values={values}
                  />
                )}
              </React.Fragment>
            );
          }
          default: {
            const mediaObjectTypeName = config.mediaObjectTypeName || config.fieldName;

            return (
              <React.Fragment key={mediaObjectTypeName}>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  {
                    <FileUploadSection
                      {...config}
                      product={product.name}
                      productEntity={product}
                      productEntityId={product.id}
                      headline={config.headline?.({ ...config, product })}
                      headlineVariant="h6"
                      headlineColor="text.primary"
                      description={config.description?.({ ...config, product })}
                      helperText={config.helperText?.({ ...config, product })}
                      updateAction={updateAction}
                      setIsBlocked={setUploadIsBlocked}
                      isBlocked={uploadIsBlocked}
                      hideUploadButton={values?.missingDocuments?.includes(mediaObjectTypeName)}
                      mediaObjectCombinationRequest={mediaObjectCombinationRequests?.[mediaObjectTypeName]!}
                      postSubmitCallback={refreshCase}
                    />
                  }
                </Grid>
                {config.isRequired?.({ product }) && !hasAtLeastOneFileOfType(mediaObjectCombinationRequests, mediaObjectTypeName) && (
                  <MissingDocumentCheckbox
                    product={product}
                    onChange={onMissingDocumentsCheckboxChange}
                    updateValues={updateValue}
                    documentType={mediaObjectTypeName}
                    values={values}
                  />
                )}
              </React.Fragment>
            );
          }
        }
      })}
    </>
  );
};
